import { getToken } from './auth'
import type { User, Tree, Person, Relationship, TreeGraph, Stats, Plan, AgentResponse, Media, FamilyEvent, Collaborator, Activity } from './types'

const BASE = '/api'

async function request<T>(path: string, opts?: RequestInit): Promise<T> {
  const token = getToken()
  const headers: Record<string, string> = {
    ...(opts?.headers as Record<string, string> || {}),
  }
  if (token) headers['Authorization'] = `Bearer ${token}`
  if (opts?.body && typeof opts.body === 'string') headers['Content-Type'] = 'application/json'

  const res = await fetch(`${BASE}${path}`, { ...opts, headers })
  if (!res.ok) {
    // expired/invalid session on an authed call → clean logout instead of broken UI
    if (res.status === 401 && token && !path.startsWith('/auth/')) {
      const { clearAuth } = await import('./auth')
      clearAuth()
      window.location.href = '/login'
    }
    const text = await res.text().catch(() => '')
    throw new Error(`API ${res.status}: ${text}`)
  }
  return res.json()
}

/** Fetch with the Authorization header, then save the response as a file — keeps
 *  credentials out of URLs/history/edge logs for downloads (D-105). */
async function authedDownload(path: string, filename: string): Promise<void> {
  const res = await fetch(`/api${path.replace(/^\/api/, '')}`, {
    headers: { Authorization: `Bearer ${getToken() || ''}` },
  })
  if (!res.ok) throw new Error(`API ${res.status}`)
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = filename
  document.body.appendChild(a); a.click(); a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 5000)
}

export const api = {
  // Auth
  login: (email: string) =>
    request<{ ok: boolean; message: string }>('/auth/login', { method: 'POST', body: JSON.stringify({ email }) }),
  /** Step-up code for a sensitive action — minted with its own purpose so it can
   *  never be replayed as a sign-in (v0.19.0). */
  requestStepUp: (email: string) =>
    request<{ ok: boolean }>('/auth/login', { method: 'POST', body: JSON.stringify({ email, purpose: 'stepup' }) }),
  sendKeepsake: (treeId: string, to: string, note?: string) =>
    request<{ ok: boolean; sent_to: string; filename: string }>('/keepsake', { method: 'POST', body: JSON.stringify({ tree_id: treeId, to, note }) }),
  treeOwners: (treeId: string) =>
    request<{ owners: { id: string; user_id: string; is_primary: number; status: string; email: string; name: string | null }[]; max_owners: number; my_role: string }>(`/trees/${treeId}/owners`),
  inviteCoKeeper: (treeId: string, email: string, code: string) =>
    request<{ ok: boolean }>(`/trees/${treeId}/owners`, { method: 'POST', body: JSON.stringify({ email, code }) }),
  answerKeeperInvite: (treeId: string, action: 'accept' | 'decline') =>
    request<{ ok: boolean }>(`/trees/${treeId}/owners`, { method: 'PUT', body: JSON.stringify({ action }) }),
  removeKeeper: (treeId: string, code: string, userId?: string) =>
    request<{ ok: boolean }>(`/trees/${treeId}/owners`, { method: 'DELETE', body: JSON.stringify({ user_id: userId, code }) }),
  verify: (email: string, code: string, inviteCode?: string) =>
    request<{ token: string; user: User }>('/auth/verify', { method: 'POST', body: JSON.stringify({ email, code, inviteCode }) }),
  me: () => request<User>('/auth/me'),
  updateProfile: (name: string) => request<User>('/auth/me', { method: 'PUT', body: JSON.stringify({ name }) }),
  updatePrefs: (prefs: { digest_opt?: boolean; tour_done?: boolean }) => request<User>('/auth/me', { method: 'PUT', body: JSON.stringify(prefs) }),
  logoutEverywhere: () => request<{ ok: boolean; message: string }>('/auth/logout-all', { method: 'POST' }),

  // Trees
  listTrees: () => request<Tree[]>('/trees'),
  createTree: (name: string, description?: string, accent?: string) =>
    request<Tree>('/trees', { method: 'POST', body: JSON.stringify({ name, description, accent }) }),
  getTree: (id: string) => request<TreeGraph>(`/trees/${id}`),
  updateTree: (id: string, patch: Partial<Pick<Tree, 'name' | 'description' | 'accent' | 'sage_scope'>>) =>
    request<Tree>(`/trees/${id}`, { method: 'PUT', body: JSON.stringify(patch) }),
  deleteTree: (id: string) => request<{ ok: boolean }>(`/trees/${id}`, { method: 'DELETE' }),

  // Persons
  createPerson: (input: Partial<Person> & { tree_id: string; first_name: string; relation?: { type: string; personId: string } }) =>
    request<Person & { possible_duplicate?: { id: string; first_name: string; last_name: string; birth_date?: string } | null }>('/persons', { method: 'POST', body: JSON.stringify(input) }),
  updatePerson: (id: string, patch: Partial<Person>) =>
    request<Person>(`/persons/${id}`, { method: 'PUT', body: JSON.stringify(patch) }),
  deletePerson: (id: string) => request<{ ok: boolean }>(`/persons/${id}`, { method: 'DELETE' }),

  // Relationships
  createRelationship: (input: { tree_id: string; type: 'spouse' | 'parent'; person1_id: string; person2_id: string; start_date?: string; notes?: string }) =>
    request<Relationship>('/relationships', { method: 'POST', body: JSON.stringify(input) }),
  deleteRelationship: (id: string) => request<{ ok: boolean }>(`/relationships?id=${id}`, { method: 'DELETE' }),
  mergePersons: (tree_id: string, keep_id: string, remove_id: string) =>
    request<{ ok: boolean; kept: string; fields_filled: number; bonds_moved: number }>('/persons/merge', { method: 'POST', body: JSON.stringify({ tree_id, keep_id, remove_id }) }),
  // Link two EXISTING tree members (kind = the linked person's role toward the anchor)
  linkKin: (tree_id: string, kind: 'parent-of' | 'child-of' | 'spouse-of' | 'sibling-of', person_id: string, anchor_id: string) =>
    request<{ ok: boolean }>('/relationships', { method: 'POST', body: JSON.stringify({ tree_id, kind, person_id, anchor_id }) }),

  // Stats
  getStats: () => request<Stats>('/stats'),

  // Sage agent (A2UI + MCP tools)
  agentChat: (messages: { role: string; content: string }[], treeId?: string) =>
    request<AgentResponse>('/ai/agent', { method: 'POST', body: JSON.stringify({ messages, treeId }) }),

  // Chat threads
  listChats: () => request<import('./types').ChatThread[]>('/chats'),
  getChat: (id: string) => request<{ id: string; tree_id?: string; title: string; messages: string }>(`/chats?id=${id}`),
  saveChat: (opts: { id?: string; tree_id?: string; title?: string; messages: unknown[] }) =>
    request<{ id: string }>('/chats', { method: 'POST', body: JSON.stringify(opts) }),
  deleteChat: (id: string) => request<{ ok: boolean }>(`/chats?id=${id}`, { method: 'DELETE' }),

  // Billing
  listPlans: async (): Promise<Plan[]> => {
    const r = await request<{ plans: Plan[]; lockin_offers?: unknown; special_until?: string } | Plan[]>('/billing/plans')
    return Array.isArray(r) ? r : r.plans
  },
  listPlansFull: () => request<{ plans: Plan[]; lockin_offers: { years: number; label: string; blurb: string }[]; special_until: string }>('/billing/plans'),
  subscribe: (plan: string) =>
    request<{ ok: boolean; status: string; message: string }>('/billing/plans', { method: 'POST', body: JSON.stringify({ plan }) }),
  checkout: (plan: string) =>
    request<{ url: string; founder: boolean; amount: number }>('/billing/checkout', { method: 'POST', body: JSON.stringify({ plan }) }),
  verifyCheckout: (session_id: string) =>
    request<{ activated: boolean; plan?: string; status?: string; kind?: string; credits?: number; already_applied?: boolean }>('/billing/verify', { method: 'POST', body: JSON.stringify({ session_id }) }),

  // Media
  listMedia: (treeId: string, personId?: string) =>
    request<Media[]>(`/media?tree_id=${treeId}${personId ? `&person_id=${personId}` : ''}`),
  uploadMedia: async (opts: { file: Blob; filename: string; treeId: string; personId?: string; personIds?: string[]; kind: string; caption?: string; encIv?: string }) => {
    const fd = new FormData()
    fd.append('file', opts.file, opts.filename)
    fd.append('tree_id', opts.treeId)
    if (opts.personId) fd.append('person_id', opts.personId)
    if (opts.personIds?.length) fd.append('person_ids', JSON.stringify(opts.personIds))
    fd.append('kind', opts.kind)
    if (opts.caption) fd.append('caption', opts.caption)
    if (opts.encIv) { fd.append('enc', '1'); fd.append('enc_iv', opts.encIv) }
    const token = getToken()
    const res = await fetch('/api/media/upload', {
      method: 'POST', body: fd,
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    })
    if (!res.ok) throw new Error(`Upload failed (${res.status}): ${await res.text().catch(() => '')}`)
    return res.json() as Promise<Media>
  },
  deleteMedia: (id: string) => request<{ ok: boolean }>(`/media/${id}`, { method: 'DELETE' }),
  /** URL usable in <img>/<video> src (JWT rides as query param since tags can't set headers) */
  mediaUrl: (id: string) => `/api/media/${id}?t=${encodeURIComponent(getToken() || '')}`,
  fetchMediaBuffer: async (id: string) => {
    const res = await fetch(`/api/media/${id}`, { headers: { 'Authorization': `Bearer ${getToken()}` } })
    if (!res.ok) throw new Error(`media fetch ${res.status}`)
    return res.arrayBuffer()
  },

  // GEDCOM
  exportGedcomUrl: (treeId: string) => `/api/gedcom?tree_id=${treeId}&t=${encodeURIComponent(getToken() || '')}`,
  importGedcom: (treeId: string, gedcom: string) =>
    request<{ imported_persons: number; imported_relationships: number }>('/gedcom', { method: 'POST', body: JSON.stringify({ tree_id: treeId, gedcom }) }),

  // Events / calendar
  listEvents: (from: string, to: string) =>
    request<{ events: FamilyEvent[]; persons: { first_name: string; last_name: string; birth_date?: string; death_date?: string; is_living: number; tree_name: string; tree_id: string }[] }>(`/events?from=${from}&to=${to}`),
  createEvent: (input: { tree_id: string; title: string; description?: string; location?: string; kind?: string; start_at: string; end_at?: string }) =>
    request<FamilyEvent>('/events', { method: 'POST', body: JSON.stringify(input) }),
  deleteEvent: (id: string) => request<{ ok: boolean }>(`/events?id=${id}`, { method: 'DELETE' }),
  rsvp: (event_id: string, status: 'going' | 'maybe' | 'no') =>
    request<{ ok: boolean }>('/events/rsvp', { method: 'POST', body: JSON.stringify({ event_id, status }) }),

  // Global search (⌘K palette)
  search: (q: string) => request<{
    persons: { id: string; first_name: string; last_name: string; birth_date?: string; is_living: number; tree_id: string; tree_name: string }[]
    trees: { id: string; name: string; accent: string; person_count: number }[]
    events: { id: string; title: string; start_at: string; kind: string; tree_name: string }[]
  }>(`/search?q=${encodeURIComponent(q)}`),

  // Atlas / geocoding
  geocode: (q: string) => request<{ found: boolean; lat?: number; lon?: number; display?: string }>(`/geocode?q=${encodeURIComponent(q)}`),

  // Vault key sharing (ciphertext only — E2EE preserved)
  createVaultShare: (opts: { tree_id: string; ciphertext: string; iv: string; salt: string; expires_hours?: number }) =>
    request<{ id: string; expires_at: string }>('/vault-shares', { method: 'POST', body: JSON.stringify(opts) }),
  redeemVaultShare: (id: string) =>
    request<{ tree_id: string; ciphertext: string; iv: string; salt: string }>('/vault-shares', { method: 'PUT', body: JSON.stringify({ id }) }),

  // iCal + one-tap Sage fixes
  icalUrl: () => `/api/events/ical?t=${encodeURIComponent(getToken() || '')}`,
  executeTool: (treeId: string, tool: string, args: Record<string, unknown>) =>
    request<import('./types').ToolReceipt>('/ai/execute', { method: 'POST', body: JSON.stringify({ treeId, tool, args }) }),

  // Circles (orgs)
  listOrgs: () => request<import('./types').Org[]>('/orgs'),
  createOrg: (name: string) => request<import('./types').Org>('/orgs', { method: 'POST', body: JSON.stringify({ name }) }),
  getOrg: (id: string) => request<{ org: import('./types').Org; my_role: string; members: import('./types').OrgMember[]; trees: { id: string; name: string; accent: string; person_count: number; org_default_role: string }[]; invites: import('./types').OrgInvite[] }>(`/orgs/${id}`),
  renameOrg: (id: string, name: string) => request<import('./types').Org>(`/orgs/${id}`, { method: 'PUT', body: JSON.stringify({ name }) }),
  dissolveOrg: (id: string) => request<{ ok: boolean }>(`/orgs/${id}`, { method: 'DELETE' }),
  createOrgInvite: (org_id: string, role: 'admin' | 'member', max_uses = 0, expires_hours = 168, require_approval = false) =>
    request<{ token: string; join_url: string }>('/orgs/invites', { method: 'POST', body: JSON.stringify({ org_id, role, max_uses, expires_hours, require_approval }) }),
  revokeOrgInvite: (token: string) => request<{ ok: boolean }>(`/orgs/invites?token=${token}`, { method: 'DELETE' }),
  previewJoin: (token: string) => request<{ org_name: string; role: string; expires_at: string; require_approval?: number; my_request_pending?: boolean }>(`/orgs/join?token=${token}`),
  joinOrg: (token: string) => request<{ ok: boolean; org_id?: string; org_name: string; role?: string; pending?: boolean; message?: string }>('/orgs/join', { method: 'POST', body: JSON.stringify({ token }) }),
  listJoinRequests: (org_id: string) => request<{ requests: { id: string; user_id: string; email: string; role: string; created_at: string; user_name: string }[] }>(`/orgs/requests?org_id=${org_id}`),
  resolveJoinRequest: (id: string, action: 'approve' | 'decline') =>
    request<{ ok: boolean; status: string }>('/orgs/requests', { method: 'POST', body: JSON.stringify({ id, action }) }),
  setMemberRole: (org_id: string, user_id: string, role: 'admin' | 'member') =>
    request<{ ok: boolean }>('/orgs/members', { method: 'PUT', body: JSON.stringify({ org_id, user_id, role }) }),
  removeMember: (org_id: string, user_id: string) =>
    request<{ ok: boolean }>(`/orgs/members?org_id=${org_id}&user_id=${user_id}`, { method: 'DELETE' }),

  // Privacy (GDPR)
  /** Authenticated download — the token travels in the header, never the URL (D-105). */
  downloadPrivacyExport: () => authedDownload('/api/privacy/export', `family-ancestry-export-${new Date().toISOString().slice(0, 10)}.json`),
  privacyErase: (confirm: string) =>
    request<{ erased: boolean; summary: Record<string, unknown>; farewell: string }>('/privacy/erase', { method: 'POST', body: JSON.stringify({ confirm }) }),

  // Admin audit
  adminAudit: (opts?: { actor?: string; before?: string }) =>
    request<import('./types').AuditRow[]>(`/admin/audit?limit=100${opts?.actor ? `&actor=${encodeURIComponent(opts.actor)}` : ''}${opts?.before ? `&before=${opts.before}` : ''}`),

  // Promo / billing codes
  myRedemptions: () => request<{ code: string; kind: string; percent_off: number; lock_years: number; locked_until?: string; applied_at: string }[]>('/promo'),
  redeemPromo: (code: string) => request<{ ok: boolean; kind: string; percent_off: number; lock_years: number; message: string }>('/promo', { method: 'POST', body: JSON.stringify({ code }) }),
  adminListPromo: () => request<{ code: string; kind: string; label: string; percent_off: number; lock_years: number; applies_to: string; max_uses: number; used_count: number; active: boolean; expires_at?: string }[]>('/admin/promo-codes'),
  adminCreatePromo: (b: { code?: string; kind: string; label?: string; percent_off?: number; lock_years?: number; applies_to?: string; max_uses?: number; expires_at?: string }) =>
    request<{ code: string }>('/admin/promo-codes', { method: 'POST', body: JSON.stringify(b) }),
  adminDeactivatePromo: (code: string) => request<{ ok: boolean }>(`/admin/promo-codes?code=${code}`, { method: 'DELETE' }),

  // Support desk
  listMyTickets: () => request<{ id: string; kind: string; subject: string; body: string; status: string; admin_note: string; created_at: string }[]>('/support'),
  fileTicket: (kind: string, subject: string, body: string) => request<{ ok: boolean; message: string }>('/support', { method: 'POST', body: JSON.stringify({ kind, subject, body }) }),
  adminListTickets: (status?: string) => request<{ id: string; reporter: string; email: string; kind: string; subject: string; body: string; status: string; admin_note: string; created_at: string }[]>(`/admin/support${status ? `?status=${status}` : ''}`),
  adminUpdateTicket: (id: string, status: string, admin_note?: string, notify_reporter?: boolean) => request<{ ok: boolean; status: string; notified: boolean }>('/admin/support', { method: 'PUT', body: JSON.stringify({ id, status, admin_note, notify_reporter }) }),
  downloadAuditExport: () => authedDownload('/api/admin/audit-export', `family-ancestry-audit-${new Date().toISOString().slice(0, 10)}.ndjson`),
  adminOnboarding: () => request<{ funnel: { registered: number; approved: number; named: number; placed: number; contributed: number; met_sage: number; toured: number; stalled: number }; members: Record<string, string | number | boolean | null>[] }>('/admin/onboarding'),

  // Admin observability
  adminInsights: () => request<{ users: number; approved_users: number; constellations: number; souls: number; circles: number; ai_messages_24h: number; off_topic_total: number; open_alerts: number; mutations_24h: number; top_askers: { who: string; msgs: number }[]; plans: { plan: string; c: number }[]; storage_total_bytes?: number; media_files?: number; media_photos?: number; media_videos?: number; scan_breakdown?: { s: string; c: number }[]; top_storage?: { who: string; bytes: number; files: number }[]; pending_claims?: number }>('/admin/insights'),
  adminAiMonitor: (flagged?: boolean) => request<{ id: string; role: string; content: string; off_topic: number; created_at: string; who: string; tree_id?: string }[]>(`/admin/ai-monitor${flagged ? '?flagged=1' : ''}`),
  adminAlerts: () => request<{ id: string; who?: string; kind: string; detail: string; severity: string; acknowledged: number; created_at: string }[]>('/admin/alerts'),
  adminAckAlert: (id: string) => request<{ ok: boolean }>('/admin/alerts', { method: 'PUT', body: JSON.stringify({ id }) }),

  // Collaborators & activity
  listCollaborators: (treeId: string) => request<Collaborator[]>(`/collaborators?tree_id=${treeId}`),
  addCollaborator: (treeId: string, email: string, role: 'viewer' | 'editor') =>
    request<Collaborator>('/collaborators', { method: 'POST', body: JSON.stringify({ tree_id: treeId, email, role }) }),
  removeCollaborator: (id: string) => request<{ ok: boolean }>(`/collaborators?id=${id}`, { method: 'DELETE' }),
  listActivity: (treeId: string) => request<Activity[]>(`/activity?tree_id=${treeId}`),

  // Admin
  adminListUsers: () => request<(User & { tree_count: number; created_at: string; last_login?: string; invite_code_used?: string })[]>('/admin/users'),
  adminApproveUser: (userId: string, approved: boolean) =>
    request<User>('/admin/users', { method: 'PUT', body: JSON.stringify({ userId, approved }) }),
  adminDeleteUser: (userId: string) => request<{ ok: boolean }>(`/admin/users?userId=${userId}`, { method: 'DELETE' }),
  adminListInviteCodes: () => request<{ code: string; label: string; max_uses: number; used_count: number; active: boolean; expires_at?: string }[]>('/admin/invite-codes'),
  adminCreateInviteCode: (opts: { code?: string; label?: string; maxUses?: number; expiresAt?: string; treeId?: string }) =>
    request<{ code: string }>('/admin/invite-codes', { method: 'POST', body: JSON.stringify(opts) }),
  adminDeactivateCode: (code: string) => request<{ ok: boolean }>(`/admin/invite-codes?code=${code}`, { method: 'DELETE' }),

  // Sage credits (v0.9.0)
  getCredits: () => request<{
    balance: number; tier: string; included_limit: number; included_used: number; resets_at: string
    auto_reload: boolean; auto_reload_pack: string
    packs: { id: string; credits: number; cents: number; discounted_cents?: number; promo_code?: string }[]
  }>('/credits'),
  setCreditPrefs: (prefs: { auto_reload?: boolean; auto_reload_pack?: string }) =>
    request<{ ok: boolean }>('/credits', { method: 'PUT', body: JSON.stringify(prefs) }),
  buyCredits: (pack: string) =>
    request<{ url: string }>('/credits', { method: 'POST', body: JSON.stringify({ pack }) }),

  // Stories (v0.9.0)
  listStories: (treeId: string, personId?: string) =>
    request<{ stories: { id: string; title: string; body: string; story_date?: string; author_name: string; created_at: string; persons: { id: string; name: string }[] }[] }>(`/stories?tree_id=${treeId}${personId ? `&person_id=${personId}` : ''}`),
  createStory: (b: { tree_id: string; title: string; body?: string; story_date?: string; person_ids?: string[] }) =>
    request<{ id: string }>('/stories', { method: 'POST', body: JSON.stringify(b) }),
  updateStory: (b: { id: string; title?: string; body?: string; story_date?: string; person_ids?: string[] }) =>
    request<{ ok: boolean }>('/stories', { method: 'PUT', body: JSON.stringify(b) }),
  deleteStory: (id: string) => request<{ ok: boolean }>(`/stories?id=${id}`, { method: 'DELETE' }),

  // Trash (v0.9.0)
  listTrash: (treeId: string) => request<{
    persons: { id: string; first_name: string; last_name: string; deleted_at: string }[]
    media: { id: string; filename: string; kind: string; deleted_at: string; size_bytes?: number; uploader_id?: string }[]
    retention_days: number
    purgeable_count: number
    purgeable_bytes: number
    reclaimable_bytes: number
  }>(`/trash?tree_id=${treeId}`),
  purgeTrash: (treeId: string, id?: string) =>
    request<{ ok: boolean; purged: number; bytes: number; failed: number }>(
      `/trash?tree_id=${treeId}&${id ? `id=${id}` : 'all=1'}`, { method: 'DELETE' }),
  restoreFromTrash: (tree_id: string, kind: 'person' | 'media', id: string) =>
    request<{ ok: boolean }>('/trash', { method: 'POST', body: JSON.stringify({ tree_id, kind, id }) }),

  // Media tags (v0.9.0)
  updateMedia: (id: string, patch: { caption?: string; person_ids?: string[] }) =>
    request<{ ok: boolean }>(`/media/${id}`, { method: 'PUT', body: JSON.stringify(patch) }),

  // Claims & placement (v0.10.0)
  fileClaim: (b: { tree_id: string; kind: 'claim' | 'join' | 'link'; person_id?: string; target_user_id?: string; note?: string }) =>
    request<{ ok: boolean; id: string; message: string }>('/claims', { method: 'POST', body: JSON.stringify(b) }),
  myClaims: () => request<{ claims: { id: string; kind: string; status: string; note: string; created_at: string; tree_name: string; person_name?: string }[] }>('/claims?mine=1'),
  treeClaims: (treeId: string) => request<{ claims: { id: string; kind: string; note: string; created_at: string; person_id?: string; person_name?: string; requester_name?: string; requester_email: string; target_name?: string; target_email?: string }[] }>(`/claims?tree_id=${treeId}`),
  resolveClaim: (id: string, action: 'approve' | 'decline') =>
    request<{ ok: boolean; status: string }>('/claims', { method: 'PUT', body: JSON.stringify({ id, action }) }),
  suggestPlacement: (b: { father_name?: string; mother_name?: string }) =>
    request<{ trees: { tree_id: string; tree_name: string; self_matches: { id: string; name: string; birth_year?: string }[]; parent_matches: { role: string; id: string; name: string; birth_year?: string }[] }[]; message: string }>('/onboarding', { method: 'POST', body: JSON.stringify(b) }),
  linkableUsers: (treeId: string) => request<{ users: { id: string; display: string }[] }>(`/onboarding?tree_id=${treeId}`),

  // Sanctum lock + privacy veil (v0.10.0)
  getLock: () => request<{ enabled: boolean; timeout_mins: number; privacy_veil: boolean }>('/auth/lock'),
  setLock: (b: { passcode?: string; current_passcode?: string; timeout_mins?: number; privacy_veil?: boolean; disable?: boolean }) =>
    request<{ ok: boolean }>('/auth/lock', { method: 'PUT', body: JSON.stringify(b) }),
  verifyLock: (passcode: string) => request<{ ok: boolean; unlocked: boolean }>('/auth/lock', { method: 'POST', body: JSON.stringify({ passcode }) }),

  // Storage (v0.10.0)
  // Headroom — every limit that can stop you, in one call (v0.25.0, D-144)
  getHeadroom: () => request<{
    plan: string
    purchases_enabled: boolean
    limits: { key: 'trees' | 'storage' | 'sage' | 'tickets'; label: string; used: number; limit: number; unit: 'count' | 'bytes' | 'messages'; extra?: Record<string, number | string> }[]
  }>('/headroom'),
  grant: (userId: string, kind: 'plan' | 'storage_mb' | 'credits', amount: string | number) =>
    request<{ ok: boolean; granted: string; email: string }>('/admin/grant', { method: 'POST', body: JSON.stringify({ userId, kind, amount }) }),

  getStorage: () => request<{ used_bytes: number; quota_bytes: number; bonus_bytes: number; plan: string; counts: { photo: number; video: number; document: number; avatar: number }; packs: { id: string; bytes: number; credits: number; label: string }[] }>('/storage'),
  buyStorage: (pack: string) => request<{ ok: boolean; bonus_bytes: number; balance: number }>('/storage', { method: 'POST', body: JSON.stringify({ pack }) }),

  // Web push (v0.9.0)
  getPushStatus: () => request<{ key: string | null; subscribed: boolean }>('/push'),
  registerNativePushToken: (platform: 'ios' | 'android', token: string) =>
    request<{ ok: boolean }>('/push', { method: 'POST', body: JSON.stringify({ native: true, platform, token }) }),
  subscribePush: (sub: { endpoint: string; keys: { p256dh: string; auth: string } }) =>
    request<{ ok: boolean }>('/push', { method: 'POST', body: JSON.stringify(sub) }),
  unsubscribePush: (endpoint: string) =>
    request<{ ok: boolean }>('/push', { method: 'DELETE', body: JSON.stringify({ endpoint }) }),
}
